/**
 * Putting a file where a row can point at one.
 *
 * A file cell holds no file. It holds the *names* of user files -- opaque
 * strings the backend hands out -- and everything else in the value is the
 * backend's serialisation of what it stored. So attaching something is two
 * steps that cannot be one: upload it, then write the name it came back with
 * into the row.
 */
import { getApi, unwrap } from '@/api/client'

/**
 * A file the backend has stored, as it describes one.
 *
 * Hand-typed for the same reason a row is: `name` and `visible_name` are the
 * two the client sends back, and the rest is what it draws with.
 */
export interface UserFileValue {
  name: string
  visible_name?: string
  original_name?: string
  size?: number
  mime_type?: string
  is_image?: boolean
  url?: string
  /**
   * The sizes the backend makes: 21 pixels tall for a grid row, 48 square for
   * a list, and 300x160 for the top of a card.
   */
  thumbnails?: {
    tiny?: { url?: string }
    small?: { url?: string }
    card_cover?: { url?: string }
  }
}

export type FileUploader = (file: File) => Promise<UserFileValue>

/**
 * Uploads one file and answers with what the backend made of it.
 *
 * Sent as `multipart/form-data`, and the client needs telling none of that:
 * it passes a `FormData` through its serialiser untouched and then leaves the
 * content type unset, because the boundary in it is the browser's to write and
 * cannot be written without the body.
 */
async function uploadToApi(file: File): Promise<UserFileValue> {
  const body = new FormData()
  body.append('file', file)

  const uploaded = await unwrap<unknown>(
    getApi().POST('/api/user-files/upload-file/', { body: body as never }),
  )

  const stored = uploaded as UserFileValue
  // The name to show, which the backend fills in from the original when the
  // row is written and does not send back with the upload itself.
  return { ...stored, visible_name: stored.visible_name ?? stored.original_name ?? '' }
}

/**
 * The seam a component test writes through.
 *
 * The same arrangement, and for the same reason, as the row fetcher's: jsdom
 * has a `FormData` and Node has a `Request`, and a `Request` refuses a
 * `FormData` from the other implementation -- it stringifies it and sends
 * `text/plain`. So a multipart request cannot be built under jsdom at all, and
 * a component test that reached this would fail on the environment rather than
 * on anything it meant to check. The real request is exercised by this
 * module's own spec, which runs outside jsdom.
 */
let uploader: FileUploader = uploadToApi

export const uploadUserFile: FileUploader = (file) => uploader(file)

/** Test seam: hand the editor an uploader, or `null` to restore the real one. */
export function __setFileUploaderForTests(replacement: FileUploader | null): void {
  uploader = replacement ?? uploadToApi
}
